import { useTranslation } from 'react-i18next';
import { PhotoPicker } from './PhotoPicker';
import styles from './StorePhotoSlots.module.css';

/** 가게 사진의 자리. 무엇을 찍어야 할지 알려 주는 이름이기도 합니다. */
export type StoreSlot = 'EXTERIOR' | 'INTERIOR' | 'SIGNATURE' | 'MENU';
export const STORE_SLOTS: StoreSlot[] = ['EXTERIOR', 'INTERIOR', 'SIGNATURE', 'MENU'];

export interface StorePhoto {
    photoId: number;
    slot: StoreSlot;
    imageUrl: string;
    likeCount?: number;
}

/**
 * 가게 사진 네 자리 — 외관 · 내부 · 대표음식 · 메뉴판.
 *
 * "사진 올려주세요" 로는 안 모입니다. 빈 자리를 보여 주면 그 자체가 부탁이 되고,
 * 무엇을 찍어야 할지도 함께 알려 줍니다. 다 찬 가게는 더 조르지 않습니다.
 *
 * 올릴 수 있는 사람에게만 빈 자리를 보여 줍니다(canAdd). 모임 진행 중이고
 * 그 장소에 도착한 사람만 해당합니다 — 그래야 실제로 그 자리에서 찍힙니다.
 */
export const StorePhotoSlots = ({
    photos, canAdd, onPick, onOpen,
}: {
    photos: StorePhoto[];
    /** 지금 사진을 올릴 수 있는 상태인가 (진행 중 + 도착) */
    canAdd?: boolean;
    onPick?: (slot: StoreSlot, files: File[]) => void;
    onOpen?: (photo: StorePhoto) => void;
}) => {
    const { t } = useTranslation();
    const bySlot = new Map(photos.map(p => [p.slot, p]));
    const emptyCount = STORE_SLOTS.filter(s => !bySlot.has(s)).length;

    return (
        <div className={styles.wrap}>
            <div className={styles.grid}>
                {STORE_SLOTS.map(slot => {
                    const photo = bySlot.get(slot);
                    const name = t(`storePhoto.slot.${slot}`);
                    if (photo) {
                        return (
                            <button
                                key={slot}
                                type="button"
                                className={styles.filled}
                                onClick={() => onOpen?.(photo)}
                            >
                                <img src={photo.imageUrl} alt={name} loading="lazy" />
                                <span className={styles.name}>{name}</span>
                            </button>
                        );
                    }
                    return (
                        <div key={slot} className={styles.cell}>
                            <PhotoPicker
                                label={name}
                                disabled={!canAdd}
                                onPick={(files) => onPick?.(slot, files)}
                            />
                        </div>
                    );
                })}
            </div>

            {/* 빈 자리가 곧 부탁입니다. 다 찼으면 아무 말도 하지 않습니다. */}
            {canAdd && emptyCount > 0 && (
                <p className={styles.ask}>{t('storePhoto.askHere', { n: emptyCount })}</p>
            )}
        </div>
    );
};
